// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import useRouter from 'Hooks/useRouter';
import usePermissions from 'Hooks/usePermissions';
import useUser from 'Hooks/useUser';
import { PERMISSION } from 'Constants';

import AppComponent from 'Components/App/App';
import ProductPage from 'Pages/Product/ProductPage';
import ProductsPage from 'Pages/Products/ProductsPage';
import CheckoutPage from 'Pages/Checkout/CheckoutPage';
import OrderPage from 'Pages/Order/OrderPage';
import ReservedProductsPage from 'Pages/ReservedProducts/ReservedProductsPage';
import SuccessCheckoutPage from 'Pages/Checkout/SuccessCheckoutPage';
import WishlistsPage from 'Pages/Lists/WishlistsPage';
import ServicePage from 'Pages/Service/ServicePage';
import Information from 'Pages/Information/Information';
import AddressesPage from 'Pages/Addresses/AddressesPage';
import OrdersPage from 'Pages/Orders/OrdersPage';
import UsersPage from 'Pages/Users/UsersPage';
import DashboardPage from 'Pages/Dashboard/DashboardPage';
import ReportsPage from 'Pages/Reports/ReportsPage';
import PromotionsPage from 'Pages/Promotions/PromotionsPage';
import APIIntegrationsPage from 'Pages/APIIntegrations/APIIntegrationsPage';
import AccountPage from 'Pages/Account/AccountPage';
import MFAPage from 'Pages/MFA/MFAPage';
import LoginPage from 'Pages/Login/LoginPage';
import RegistrationPage from 'Pages/Registration/RegistrationPage';
import PasswordChangePage from 'Pages/PasswordChange/PasswordChangePage';
import ActivateUserPage from 'Pages/ActivateUser/ActivateUserPage';
import PromotionTemplatesPage from 'Pages/PromotionTemplates/PromotionTemplatesPage';
import NotFoundPage from 'Pages/NotFound/NotFoundPage';
import OfferEditorPage from 'Pages/Offers/OfferEditorPage';
import OffersPage from 'Pages/Offers/OffersPage';

let unsubscribe = null;

const App = memo(() => {
  const { location, history } = useRouter();
  const { can } = usePermissions();
  const {
    data: { isImpersonated } = {},
    isLoading: userLoading
  } = useUser();
  const accountFeatures = can(PERMISSION.ACCESS_ACCOUNT_FEATURES);
  const manageAddresses = can(PERMISSION.MANAGE_ADDRESSES);
  const manageUsers = can(PERMISSION.MANAGE_USERS);
  const notAuthorized = <Navigate to="/not-found?type=403" replace />;
  let offersRoutes = (
    <Route path="offers">
      <Route index element={ <OffersPage /> } />
      <Route path="private" element={ <OffersPage /> } />
      <Route path="private/:id/editor" element={ <OfferEditorPage isPrivate /> } />
      <Route path=":id/editor" element={ <OfferEditorPage /> } />
    </Route>
  );

  if (isImpersonated || !accountFeatures) {
    offersRoutes = <Route path="offers/*" element={ <Navigate to="/not-found" replace /> } />;
  }
  if (userLoading || isImpersonated === undefined) {
    offersRoutes = <Route path="offers/*" element={ null } />;
  }

  if (!unsubscribe) {
    unsubscribe = history.listen((oldLoc, newLoc, action) => {
      const { search = '', pathname = '' } = (newLoc || {});
      if (action === 'PUSH') {
        if (
          typeof window !== 'undefined'
          && window.location.pathname.includes('products')
          // eslint-disable-next-line
          && window.location.pathname.match(/^\/products(\/[\w+?!'"_\\-]+)?(\/attributes(\/[\w+?,!'"_\\-]+)+)?(\/brands\/[\w\d\s\-]+)?(\/price\/[\d,]+)?(\/search\/[\d\w\s"',\/\\_\-+]+)?(\/e\/)?(\/c\/)?/g)
          && !window.location.pathname.includes('/pd/')
        ) {
          const filteringMode = search?.match(/filtering=+(\w+)/);
          const newPage = search?.match(/page=+(\d)/);
          const { search: oldSearch = '' } = (oldLoc || {});
          const oldPage = oldSearch?.match(/page=+(\d)/);
          if (
            (newPage?.at(1) === oldPage?.at(1))
            && (
              !filteringMode
              || filteringMode?.at(1) === 'instant'
            )
          ) {
            return;
          }
        }
        window.scrollTo(0, 0);
      }
      if (process.env.NODE_ENV === 'development') {
        console.log(`Path changed to: %c ${ pathname } from action ${ action }`, 'font-weight: bold');
      }
    });
  }

  useEffect(() => {
    return () => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
    };
  }, []);

  // if (isLoading) {
  //   return null;
  // }

  return (
    <Routes location={ location }>
      <Route path="" element={ <AppComponent /> }>
        <Route exact path="/" element={ <DashboardPage /> } />
        <Route path="account/*" element={ <AccountPage /> } />
        <Route path="products">
          <Route index element={ <ProductsPage /> } />
          <Route path="*" element={ <ProductsPage /> } />
          <Route path=":slug/pd/:id" element={ <ProductPage /> } />
        </Route>
        <Route path="promotions" element={ <PromotionsPage /> } />
        <Route path="checkout">
          <Route index element={ accountFeatures ? <CheckoutPage /> : notAuthorized } />
          <Route path="success" element={ <SuccessCheckoutPage /> } />
        </Route>
        <Route path="orders">
          <Route index element={ accountFeatures ? <OrdersPage /> : notAuthorized } />
          <Route path="reserved-products" element={ accountFeatures ? <ReservedProductsPage /> : notAuthorized } />
          <Route path=":id" element={ accountFeatures ? <OrderPage /> : notAuthorized } />
        </Route>
        <Route path="lists">
          <Route index element={ <WishlistsPage /> } />
          <Route path=":id" element={ <WishlistsPage /> } />
        </Route>
        { offersRoutes }
        <Route path="service/*" element={ accountFeatures ? <ServicePage /> : notAuthorized } />
        <Route path="reports/*" element={ accountFeatures ? <ReportsPage /> : notAuthorized } />
        <Route path="addresses" element={ manageAddresses ? <AddressesPage /> : notAuthorized } />
        <Route path="users" element={ manageUsers ? <UsersPage /> : notAuthorized } />
        <Route path="api-integrations/*" element={ accountFeatures ? <APIIntegrationsPage /> : notAuthorized } />
        <Route path="information">
          <Route index element={ <Navigate to="/" replace /> } />
          <Route path=":id" element={ accountFeatures ? <Information /> : notAuthorized } />
        </Route>
        <Route path="pages/:id" element={ <PromotionTemplatesPage /> } />
        <Route path="not-found" element={ <NotFoundPage /> } />
        <Route path="*" element={ <Navigate to="/not-found" replace /> } />
      </Route>

      {/* This route is outside, so it will NOT use AppComponent's Outlet */}
      <Route path="mfa" element={ <MFAPage /> } />
      <Route path="login" element={ <LoginPage /> } />
      <Route path="registration" element={ <RegistrationPage /> } />
      <Route path="account/finalize-password-recovery/:id" element={ <PasswordChangePage /> } />
      <Route path="account/activate-user/:id" element={ <ActivateUserPage /> } />
    </Routes>
  );
});

App.displayName = 'AppContainer';

export default App;
